import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { TagsOutlined, CheckOutlined } from "@ant-design/icons";
import { PiSuitcaseRolling } from "react-icons/pi";
import { SlHandbag } from "react-icons/sl";
import { useAppContext } from "../../../contexts/AppContext";
import { FlightLegBlock } from "../../FlightBookingFlowMockup/flightBookingMockup/FlightOfferResultCardLayout";
import "../../FlightBookingFlowMockup/flightBookingMockup/FlightOfferResultCard.css";
import {
  packageLegToFlightLegBlockLeg,
  resolveAirlineNameFromPackageLeg,
} from "./packageFlightLegAdapter";
import { formatPackageLegSeatsHe } from "../../../utils/premiumPackageCardModel";
import "./PackageFlightLegUnified.css";

function PackageLegFooter({
  leg,
  typeBadgeExtra,
  airlineName,
  packagePremiumLayout = false,
  suppressLuggageChips = false,
  suppressStatusRemark = false,
  /** קוד IATA ללוגו בכרטיס — כשקיים, אין להציג שוב קידומת מספר הטיסה (למשל A3) */
  carrierIataCode = null,
}) {
  const hand = Number(leg?.maxHandLuggageWeight) || 0;
  const checked = Number(leg?.maxCheckedLuggageWeight) || 0;

  const chips = [];
  if (typeBadgeExtra && !packagePremiumLayout) {
    chips.push({
      key: "type",
      node: (
        <span className="pfu__chip pfu__chip--flight-type" dir="rtl">
          <TagsOutlined className="pfu__chip-icon" aria-hidden />
          {typeBadgeExtra}
        </span>
      ),
    });
  }
  if (!suppressLuggageChips && hand > 0) {
    chips.push({
      key: "hand",
      node: (
        <span
          className="pfu__chip pfu__chip--baggage pfu__chip--baggage-hand"
          dir="rtl"
        >
          <CheckOutlined className="pfu__chip-check" aria-hidden />
          <SlHandbag className="pfu__chip-icon" size={13} aria-hidden />
          <span className="pfu__chip-text">
            תיק יד {hand} ק&quot;ג
          </span>
        </span>
      ),
    });
  }
  if (!suppressLuggageChips && checked > 0) {
    chips.push({
      key: "checked",
      node: (
        <span
          className="pfu__chip pfu__chip--baggage pfu__chip--baggage-checked"
          dir="rtl"
        >
          <CheckOutlined className="pfu__chip-check" aria-hidden />
          <PiSuitcaseRolling className="pfu__chip-icon" size={13} aria-hidden />
          <span className="pfu__chip-text">
            מזוודה {checked} ק&quot;ג
          </span>
        </span>
      ),
    });
  }

  const remark = leg?.statusRemark
    ? String(leg.statusRemark).trim()
    : "";
  const seatsLine = formatPackageLegSeatsHe(leg);
  const flightMarketingCodeMatch = String(leg?.flightNumber || "")
    .trim()
    .toUpperCase()
    .match(/^[A-Z]{1,3}/);
  const flightMarketingCode = flightMarketingCodeMatch?.[0] || "";
  const legCabinClass =
    leg?.flightClass != null && String(leg.flightClass).trim() !== ""
      ? String(leg.flightClass).trim()
      : leg?.flightCategory != null && String(leg.flightCategory).trim() !== ""
        ? String(leg.flightCategory).trim()
        : "";
  const carrierCodeNorm =
    carrierIataCode != null && String(carrierIataCode).trim() !== ""
      ? String(carrierIataCode).trim().toUpperCase()
      : "";
  /**
   * קידומת מספר טיסה (A3 וכו') מיותרת בשורת המטא כשכבר יש קוד נושא ללוגו בכרטיס.
   * בלי קוד נושא — משאירים את הקידומת כרמז חזותי אם הטיסה בפורמט קלאסי.
   */
  const includeFlightMarketingCode =
    Boolean(flightMarketingCode) && !carrierCodeNorm;
  const airlineSummaryParts = packagePremiumLayout
    ? [
        includeFlightMarketingCode ? flightMarketingCode : null,
        typeBadgeExtra || null,
        legCabinClass ? `מחלקת ${legCabinClass}` : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="pfu__footer-inner" dir="rtl">
      {chips.length > 0 ? (
        <div className="pfu__chip-row" role="list">
          {chips.map(({ key, node }) => (
            <span key={key} className="pfu__chip-wrap" role="listitem">
              {node}
            </span>
          ))}
        </div>
      ) : null}
      {!packagePremiumLayout && airlineName ? (
        <p className="pfu__airline-name" dir="rtl">
          {airlineName}
        </p>
      ) : null}
      {packagePremiumLayout && (airlineName || airlineSummaryParts.length > 0) ? (
        <div className="pfu__airline-meta-row" dir="rtl">
          {airlineName ? <span className="pfu__airline-name">{airlineName}</span> : null}
          {airlineSummaryParts.length > 0 ? (
            <span className="pfu__airline-summary">{airlineSummaryParts.join(" · ")}</span>
          ) : null}
        </div>
      ) : null}
      {seatsLine ? (
        <p className="pfu__seats-line" dir="rtl">
          {seatsLine}
        </p>
      ) : null}
      {remark && !suppressStatusRemark ? (
        <Tooltip title={remark.length > 120 ? remark : undefined}>
          <p className="pfu__remark">{remark}</p>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * כרטיס כיוון טיסה בחבילת נופש — שלד ויזואלי כמו מנוע הטיסות + כל שכבת המידע מה־LDS.
 */
export default function PackageFlightLegUnified({
  leg,
  labelHe = "הלוך",
  showDivider = false,
  legIndex = 0,
  /** כאשר מציגים פירוט מטען מחוץ לכרטיס (למשל FlightFooterRow) */
  suppressLuggageChips = false,
  suppressStatusRemark = false,
  packagePremiumLayout = false,
}) {
  const { airlines } = useAppContext();

  const { mapped, typeBadgeExtra, rawLeg } = useMemo(
    () =>
      packageLegToFlightLegBlockLeg(leg, { labelHe, airlines: airlines || [] }),
    [leg, labelHe, airlines]
  );

  const airlineName = useMemo(
    () => resolveAirlineNameFromPackageLeg(leg, airlines || []),
    [leg, airlines]
  );

  if (!mapped) return null;

  const footerSlot = (
    <PackageLegFooter
      leg={rawLeg}
      typeBadgeExtra={typeBadgeExtra}
      airlineName={airlineName}
      packagePremiumLayout={packagePremiumLayout}
      suppressLuggageChips={suppressLuggageChips}
      suppressStatusRemark={suppressStatusRemark}
      carrierIataCode={mapped?.airlineCode ?? null}
    />
  );

  return (
    <FlightLegBlock
      leg={mapped}
      showDivider={showDivider}
      showSupplierInMeta={false}
      legIndex={legIndex}
      footerSlot={footerSlot}
      packagePremiumLayout={packagePremiumLayout}
    />
  );
}
